import React, { useCallback, useEffect, useMemo, useState } from "react";
import { X, Plus, RotateCw, Save, Trash2, Edit, Settings } from "lucide-react";
import OperatorCardActions from "../components/OperatorCardActions";
import FilterPopover from "../components/FilterPopover";
import ToolbarSearch from "../components/ToolbarSearch";
import {
    applyBillingImport,
    getBillingSettings,
    listBillingExpenseItems,
    listClientBilling,
    listLocationBillingSettings,
    previewBillingImport,
    updateBillingSettings,
    updateBillingExpenseItems,
    updateLocationBillingSettings,
    upsertClientBilling,
} from "../services/billing";

const formatMoney = (v) => {
    const n = Number(v);
    const safe = Number.isFinite(n) ? n : 0;
    return safe.toFixed(2);
};

const toNumber = (v) => {
    const n = Number(String(v).replace(",", "."));
    return Number.isFinite(n) ? n : 0;
};

const getAccruedAmount = (pricePerM2, areaM2) => toNumber(pricePerM2) * toNumber(areaM2);

const getBalanceAmount = ({ price_per_m2, area_m2, paid_amount, debt_amount }) => (
    getAccruedAmount(price_per_m2, area_m2) - toNumber(paid_amount) + toNumber(debt_amount)
);

const getBalancePresentation = (balanceAmount) => {
    const balance = toNumber(balanceAmount);
    if (balance > 0) {
        return {
            label: "Недоплачено",
            amount: balance,
            color: "#b42318",
        };
    }
    if (balance < 0) {
        return {
            label: "Переплачено",
            amount: Math.abs(balance),
            color: "#067647",
        };
    }
    return {
        label: "Оплачено",
        amount: 0,
        color: "#344054",
    };
};

const makeExpenseDraft = (item = {}) => ({
    id: item.id ?? null,
    localKey: item.localKey || `expense-${item.id ?? "new"}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title: item.title ?? "",
    description: item.description ?? "",
    amount_per_m2: item.amount_per_m2 ?? 0,
    position: item.position ?? 0,
    status: item.status !== false,
});

const BillingPanelShell = ({ open, onClose, title, children }) => {
    if (!open) return null;
    return (
        <div className="modal-backdrop billing-panel-backdrop" onClick={onClose} role="presentation">
            <div
                className="modal-content billing-panel-modal operator-mobile-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={title}
            >
                <div className="wr-content-view-modal__head">
                    <h3 className="wr-content-view-modal__title">{title}</h3>
                    <button type="button" className="wr-content-view-modal__close" onClick={onClose} aria-label="Закрити">
                        ✕
                    </button>
                </div>
                <div className="billing-panel-modal__body">{children}</div>
            </div>
        </div>
    );
};

const OperatorBilling = () => {
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const [price, setPrice] = useState("0");
    const [effectiveNetworkPrice, setEffectiveNetworkPrice] = useState(0);
    const [expensesNetworkPrice, setExpensesNetworkPrice] = useState(0);
    const [usesExpenseItems, setUsesExpenseItems] = useState(false);
    const [expenseItems, setExpenseItems] = useState([]);
    const [showExpenseEditor, setShowExpenseEditor] = useState(false);
    const [locationRows, setLocationRows] = useState([]);
    const [locationEdits, setLocationEdits] = useState({});
    const [showLocationPrices, setShowLocationPrices] = useState(false);
    const [rows, setRows] = useState([]);
    const [edits, setEdits] = useState({});
    const [editingRowKey, setEditingRowKey] = useState(null);
    const [importFile, setImportFile] = useState(null);
    const [importPreview, setImportPreview] = useState(null);
    const [selectedImportRows, setSelectedImportRows] = useState({});
    const [importBusy, setImportBusy] = useState(false);
    const [showReservePanel, setShowReservePanel] = useState(false);
    const [showImportPanel, setShowImportPanel] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [tariffSummaryOpen, setTariffSummaryOpen] = useState(false);
    const [expandedBillingKey, setExpandedBillingKey] = useState(null);

    // --- Пошук та фільтри (аналогічно ClientsOperators) ---
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState({ apartment: "", location: "" });
    const [filteredRows, setFilteredRows] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        setMessage("");
        try {
            const [s, list, locs, expenses] = await Promise.all([getBillingSettings(), listClientBilling(), listLocationBillingSettings(), listBillingExpenseItems()]);
            setPrice(String(s?.manual_price_per_m2 ?? s?.price_per_m2 ?? 0));
            setEffectiveNetworkPrice(toNumber(s?.effective_price_per_m2 ?? expenses?.effective_price_per_m2 ?? s?.price_per_m2 ?? 0));
            setExpensesNetworkPrice(toNumber(expenses?.expenses_price_per_m2 ?? s?.expenses_price_per_m2 ?? 0));
            setUsesExpenseItems(Boolean(expenses?.uses_expense_items ?? s?.uses_expense_items));
            setExpenseItems((Array.isArray(expenses?.items) ? expenses.items : []).map((item, index) => makeExpenseDraft({ ...item, position: index })));
            setRows(Array.isArray(list) ? list : []);
            setLocationRows(Array.isArray(locs) ? locs : []);
            setEdits({});
            setEditingRowKey(null);
            setLocationEdits({});
        } catch (e) {
            setMessage("❌ " + (e?.message || "Не вдалося завантажити оплату"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const savePrice = async () => {
        setLoading(true);
        setMessage("");
        try {
            const n = toNumber(price);
            await updateBillingSettings({ price_per_m2: n });
            await load();
            setMessage("✅ Ціну за м² збережено");
        } catch (e) {
            setMessage("❌ " + (e?.message || "Не вдалося зберегти"));
        } finally {
            setLoading(false);
        }
    };

    const setExpenseField = (localKey, field, value) => {
        setExpenseItems((prev) => prev.map((item) => item.localKey === localKey ? { ...item, [field]: value } : item));
    };

    const addExpenseItem = () => {
        setExpenseItems((prev) => [...prev, makeExpenseDraft({ position: prev.length, amount_per_m2: 0, status: true })]);
        setShowExpenseEditor(true);
    };

    const removeExpenseItem = (localKey) => {
        setExpenseItems((prev) => prev.filter((item) => item.localKey !== localKey).map((item, index) => ({ ...item, position: index })));
    };

    const saveExpenseItems = async () => {
        setLoading(true);
        setMessage("");
        try {
            await updateBillingExpenseItems({
                items: expenseItems.map((item, index) => ({
                    id: item.id ?? undefined,
                    title: item.title,
                    description: item.description || null,
                    amount_per_m2: toNumber(item.amount_per_m2),
                    position: index,
                    status: item.status !== false,
                })),
            });
            await load();
            setMessage("✅ Статті тарифу збережено");
        } catch (e) {
            setMessage("❌ " + (e?.message || "Не вдалося зберегти статті тарифу"));
        } finally {
            setLoading(false);
        }
    };

    const setLocationField = (locationId, value) => {
        setLocationEdits((p) => ({
            ...p,
            [locationId]: value,
        }));
    };

    const saveLocationPrice = async (row) => {
        const locationId = row.location_id;
        const value = locationEdits[locationId];
        const fallback = row.updated_at ? (row.price_per_m2 ?? 0) : effectiveNetworkPrice;
        const n = toNumber(value ?? fallback ?? 0);

        setLoading(true);
        setMessage("");
        try {
            await updateLocationBillingSettings({ location_id: locationId, price_per_m2: n });
            await load();
            setShowLocationPrices(false);
            setMessage("✅ Ціну за м² для локації збережено");
        } catch (e) {
            setMessage("❌ " + (e?.message || "Не вдалося зберегти"));
        } finally {
            setLoading(false);
        }
    };

    const getLocationLabelById = (locationId) => {
        if (locationId === null || locationId === undefined || locationId === "") return "";
        const id = parseInt(locationId);
        const loc = locationRows.find((l) => parseInt(l.location_id) === id);
        if (!loc) return "";
        return [loc.street, loc.house_number, loc.location_name].filter(Boolean).join(" ");
    };

    const getLocationOptionsUsedByClients = () => {
        const ids = new Set();
        let hasNull = false;
        for (const r of rows) {
            if (r.location_id === null || r.location_id === undefined || r.location_id === "") {
                hasNull = true;
            } else {
                ids.add(parseInt(r.location_id));
            }
        }

        const opts = locationRows
            .filter((loc) => ids.has(parseInt(loc.location_id)))
            .sort((a, b) => {
                const la = [a.street, a.house_number, a.location_name].filter(Boolean).join(" ").toLowerCase();
                const lb = [b.street, b.house_number, b.location_name].filter(Boolean).join(" ").toLowerCase();
                return la.localeCompare(lb, undefined, { numeric: true });
            });
        return { opts, hasNull };
    };

    const getApartmentOptions = (locationValue) => {
        const apartments = new Set();
        const isNullLocation = locationValue === "null";
        const hasLocationFilter = !!locationValue;
        const locationId = hasLocationFilter && !isNullLocation ? parseInt(locationValue) : null;

        for (const r of rows) {
            if (hasLocationFilter) {
                if (isNullLocation) {
                    if (!(r.location_id === null || r.location_id === undefined || r.location_id === "")) continue;
                } else {
                    if (r.location_id === null || r.location_id === undefined || r.location_id === "") continue;
                    if (parseInt(r.location_id) !== locationId) continue;
                }
            }

            const apt = (r.apartment || "").toString();
            if (apt) apartments.add(apt);
        }

        return [...apartments].sort((a, b) => a.toString().localeCompare(b.toString(), undefined, { numeric: true }));
    };

    const applyFilters = (searchValue, filterValues) => {
        let result = [...rows];
        const lower = (searchValue || "").toString().toLowerCase().trim();

        result = result.filter((r) => {
            const searchMatch = !lower
                ? true
                : (
                    (r.client_name || "").toString().toLowerCase().includes(lower) ||
                    (r.personal_account || "").toString().toLowerCase().includes(lower) ||
                    (r.apartment || "").toString().toLowerCase().includes(lower) ||
                    (r.location_name || "").toString().toLowerCase().includes(lower) ||
                    getLocationLabelById(r.location_id).toLowerCase().includes(lower)
                );

            const hasAnyRowFilters = !!(filterValues.location || filterValues.apartment);
            const rowMatch = !hasAnyRowFilters
                ? true
                : (() => {
                    if (filterValues.apartment && (r.apartment || "") !== filterValues.apartment) return false;

                    if (filterValues.location) {
                        if (filterValues.location === "null") {
                            return r.location_id === null || r.location_id === undefined || r.location_id === "";
                        }
                        if (r.location_id === null || r.location_id === undefined || r.location_id === "") return false;
                        return parseInt(r.location_id) === parseInt(filterValues.location);
                    }

                    return true;
                })();

            return searchMatch && rowMatch;
        });

        setFilteredRows(result);
    };

    const handleSearch = (value) => {
        setSearch(value);
        applyFilters(value, filters);
    };

    const handleFilterChange = (field, value) => {
        const updated = { ...filters, [field]: value };
        if (field === "location") {
            updated.apartment = "";
        }
        setFilters(updated);
        applyFilters(search, updated);
    };

    useEffect(() => {
        applyFilters(search, filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, locationRows]);

    const visible = useMemo(() => filteredRows, [filteredRows]);

    const getRowEditKey = (row) => `${row.client_id}:${row.context_id ?? "legacy"}:${row.location_id ?? "none"}:${row.apartment ?? ""}`;

    const setField = (row, field, value) => {
        const editKey = getRowEditKey(row);
        setEdits((p) => ({
            ...p,
            [editKey]: {
                ...(p[editKey] || {}),
                [field]: value,
            },
        }));
    };

    const startEditRow = (row) => {
        const editKey = getRowEditKey(row);
        setEditingRowKey(editKey);
        setExpandedBillingKey(editKey);
        setEdits((prev) => ({
            ...prev,
            [editKey]: {
                area_m2: row.area_m2 ?? 0,
                paid_amount: row.paid_amount ?? 0,
                debt_amount: row.debt_amount ?? 0,
            },
        }));
    };

    const toggleBillingExpanded = (editKey, isEditing) => {
        if (isEditing) return;
        setExpandedBillingKey((prev) => (prev === editKey ? null : editKey));
    };

    const openBillingPanel = (panel) => {
        setShowSettingsModal(false);
        setShowExpenseEditor(panel === "expenses");
        setShowImportPanel(panel === "import");
        setShowReservePanel(panel === "reserve");
        setShowLocationPrices(panel === "locations");
    };

    const cancelEditRow = (row) => {
        const editKey = getRowEditKey(row);
        setEditingRowKey((current) => (current === editKey ? null : current));
        setEdits((prev) => {
            const next = { ...prev };
            delete next[editKey];
            return next;
        });
    };

    const saveClient = async (row) => {
        const clientId = row.client_id;
        const patch = edits[getRowEditKey(row)] || {};
        const area_m2 = toNumber(patch.area_m2 ?? row.area_m2 ?? 0);
        const paid_amount = toNumber(patch.paid_amount ?? row.paid_amount ?? 0);
        const debt_amount = toNumber(patch.debt_amount ?? row.debt_amount ?? 0);

        setLoading(true);
        setMessage("");
        try {
            await upsertClientBilling({ client_id: clientId, context_id: row.context_id ?? null, area_m2, paid_amount, debt_amount });
            await load();
            setEditingRowKey(null);
            setMessage("✅ Збережено");
        } catch (e) {
            setMessage("❌ " + (e?.message || "Не вдалося зберегти"));
        } finally {
            setLoading(false);
        }
    };

    const handleImportFileChange = (event) => {
        const nextFile = event?.target?.files?.[0] || null;
        setImportFile(nextFile);
        setImportPreview(null);
        setSelectedImportRows({});
    };

    const previewImport = async () => {
        if (!importFile) {
            setMessage("❌ Обери CSV/TSV файл виписки");
            return;
        }

        setImportBusy(true);
        setMessage("");
        try {
            const preview = await previewBillingImport(importFile);
            const defaults = {};
            for (const item of preview?.items || []) {
                if (item?.matched && item?.client_id) {
                    defaults[item.row_index] = true;
                }
            }
            setImportPreview(preview);
            setSelectedImportRows(defaults);
            setMessage(`✅ Виписку зчитано: ${preview?.matched_rows || 0} рядків готово до рознесення`);
        } catch (e) {
            setImportPreview(null);
            setSelectedImportRows({});
            setMessage("❌ " + (e?.message || "Не вдалося зчитати виписку"));
        } finally {
            setImportBusy(false);
        }
    };

    const toggleImportRow = (rowIndex, checked) => {
        setSelectedImportRows((prev) => ({
            ...prev,
            [rowIndex]: checked,
        }));
    };

    const applyImport = async () => {
        const selectedItems = (importPreview?.items || [])
            .filter((item) => item?.matched && item?.client_id && selectedImportRows[item.row_index])
            .map((item) => ({
                row_index: item.row_index,
                client_id: item.client_id,
                context_id: item.context_id || null,
                amount: item.amount,
                purpose: item.purpose || null,
                payment_date: item.payment_date || null,
            }));

        if (!selectedItems.length) {
            setMessage("❌ Немає вибраних рядків для рознесення");
            return;
        }

        setImportBusy(true);
        setMessage("");
        try {
            const result = await applyBillingImport({
                file_name: importPreview?.file_name || importFile?.name || null,
                items: selectedItems,
            });
            await load();
            setImportPreview(null);
            setSelectedImportRows({});
            setImportFile(null);
            setMessage(`✅ Рознесено ${result?.applied_rows || 0} рядків на суму ${formatMoney(result?.total_amount || 0)} грн`);
        } catch (e) {
            setMessage("❌ " + (e?.message || "Не вдалося рознести оплату"));
        } finally {
            setImportBusy(false);
        }
    };

    const selectedImportCount = useMemo(
        () => (importPreview?.items || []).filter((item) => item?.matched && selectedImportRows[item.row_index]).length,
        [importPreview, selectedImportRows],
    );

    const selectedImportAmount = useMemo(
        () => (importPreview?.items || []).reduce((sum, item) => {
            if (!item?.matched || !selectedImportRows[item.row_index]) return sum;
            return sum + toNumber(item.amount);
        }, 0),
        [importPreview, selectedImportRows],
    );

    return (
        <div>
            <div className="config-header">
                <div>
                    <h2>Оплати</h2>
                </div>
                <div className="header-buttons">
                    <button
                        className="add-btn toolbar-icon-btn"
                        type="button"
                        onClick={() => setShowSettingsModal(true)}
                        title="Налаштування"
                        aria-label="Налаштування"
                    >
                        <Settings size={16} />
                    </button>
                    <button className="add-btn refresh toolbar-icon-btn" onClick={load} disabled={loading} title="Оновити" aria-label="Оновити">
                        <RotateCw size={16} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
                    </button>
                    <FilterPopover
                        active={Boolean(filters.location || filters.apartment)}
                        title="Фільтри оплат"
                        buttonTitle="Фільтри оплат"
                        onClear={() => {
                            const cleared = { apartment: "", location: "" };
                            setFilters(cleared);
                            applyFilters(search, cleared);
                        }}
                    >
                        <label className="toolbar-filter-field">
                            <span>Локація</span>
                            <select value={filters.location} onChange={(e) => handleFilterChange("location", e.target.value)}>
                                <option value="">Усі</option>
                                {(() => {
                                    const { opts, hasNull } = getLocationOptionsUsedByClients();
                                    return (
                                        <>
                                            {hasNull && <option value="null">Без локації</option>}
                                            {opts.map((loc) => (
                                                <option key={loc.location_id} value={loc.location_id}>
                                                    {[loc.street, loc.house_number, loc.location_name].filter(Boolean).join(" ")}
                                                </option>
                                            ))}
                                        </>
                                    );
                                })()}
                            </select>
                        </label>
                        <label className="toolbar-filter-field">
                            <span>Квартира</span>
                            <select value={filters.apartment} onChange={(e) => handleFilterChange("apartment", e.target.value)}>
                                <option value="">Усі</option>
                                {getApartmentOptions(filters.location).map((a) => (
                                    <option key={a} value={a}>{a}</option>
                                ))}
                            </select>
                        </label>
                    </FilterPopover>
                    <ToolbarSearch
                        value={search}
                        onChange={handleSearch}
                        placeholder={isMobile ? "Пошук оплати" : "Пошук по імені / о/р / локації / квартирі"}
                    />
                </div>
            </div>

            {showSettingsModal ? (
                <div className="modal-backdrop" onClick={() => setShowSettingsModal(false)} role="presentation">
                    <div
                        className="modal-content operator-mobile-modal operator-form-modal"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Налаштування оплат"
                    >
                        <h3 className="mb-2 font-semibold">Налаштування оплат</h3>
                        <div className="billing-action-grid" style={{ marginBottom: 0 }}>
                            <button
                                className={`billing-action-btn ${showExpenseEditor ? "is-open" : ""}`}
                                type="button"
                                onClick={() => openBillingPanel("expenses")}
                            >
                                Статті тарифу
                            </button>
                            <button
                                className={`billing-action-btn ${showImportPanel ? "is-open" : ""}`}
                                type="button"
                                onClick={() => openBillingPanel("import")}
                            >
                                Імпорт виписки
                            </button>
                            <button
                                className={`billing-action-btn ${showReservePanel ? "is-open" : ""}`}
                                type="button"
                                onClick={() => openBillingPanel("reserve")}
                            >
                                Резервна ціна
                            </button>
                            <button
                                className={`billing-action-btn ${showLocationPrices ? "is-open" : ""}`}
                                type="button"
                                onClick={() => openBillingPanel("locations")}
                            >
                                Ціна локації
                            </button>
                        </div>
                        <div className="modal-actions" style={{ marginTop: 14 }}>
                            <button type="button" className="add-btn cancel-btn" onClick={() => setShowSettingsModal(false)}>
                                Закрити
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            <div className={`operator-toolbar-card billing-tariff-summary ${tariffSummaryOpen ? "is-open" : ""}`}>
                <button
                    type="button"
                    className="billing-tariff-summary__toggle"
                    onClick={() => setTariffSummaryOpen((v) => !v)}
                    onPointerUp={(e) => e.currentTarget.blur()}
                    aria-expanded={tariffSummaryOpen}
                >
                    <span className="billing-tariff-summary__title">
                        Тариф мережі
                        <small>{formatMoney(effectiveNetworkPrice)} грн/м²</small>
                    </span>
                    <span className="billing-tariff-summary__caret" aria-hidden="true">
                        {tariffSummaryOpen ? "▾" : "▸"}
                    </span>
                </button>
                {tariffSummaryOpen ? (
                    <div className="operator-summary-chips" style={{ marginTop: 10 }}>
                        <div className="operator-summary-chip operator-summary-chip--open"><strong>Діючий тариф</strong> {formatMoney(effectiveNetworkPrice)} грн/м²</div>
                        <div className="operator-summary-chip operator-summary-chip--closed"><strong>За статтями</strong> {formatMoney(expensesNetworkPrice)} грн/м²</div>
                        <div className="operator-summary-chip operator-summary-chip--muted"><strong>Резервний</strong> {formatMoney(price)} грн/м²</div>
                    </div>
                ) : null}
            </div>

            <BillingPanelShell
                open={showReservePanel}
                onClose={() => setShowReservePanel(false)}
                title="Резервна ціна за м²"
            >
                <div className="form-card billing-controls" style={{ marginBottom: isMobile ? 0 : 12 }}>
                    <div className="billing-controls-row" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <strong>Резервна ціна за м² для мережі:</strong>
                        <input
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            className="billing-input"
                            style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd" }}
                        />
                        <button className="add-btn" onClick={savePrice} disabled={loading}>Зберегти</button>
                        <button className="add-btn" onClick={load} disabled={loading}>Оновити</button>
                    </div>
                    <div className="billing-help-text" style={{ fontSize: 12, marginTop: 6 }}>
                        Якщо для локації задано свою ціну — вона має пріоритет. Якщо є активні статті витрат, саме вони формують тариф мережі, а це поле лишається резервним fallback.
                    </div>
                </div>
            </BillingPanelShell>

            <BillingPanelShell
                open={showExpenseEditor}
                onClose={() => setShowExpenseEditor(false)}
                title="Статті витрат тарифу"
            >
                <div className="list-card operator-mobile-compact-card" style={{ marginBottom: isMobile ? 0 : 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <div>
                            {!isMobile ? <h3 style={{ marginTop: 0, marginBottom: 4 }}>Статті витрат тарифу</h3> : null}
                            <div className="billing-help-text" style={{ fontSize: 12 }}>
                                Додай складові тарифу за м²: ліфт, освітлення, вивіз сміття, прибирання та інші. Активні статті автоматично формують тариф мережі.
                            </div>
                        </div>
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                        <div style={{ padding: "6px 10px", borderRadius: 999, background: "#f2f4f7", fontSize: 12, fontWeight: 700 }}>
                            Діючий тариф: {formatMoney(effectiveNetworkPrice)} грн/м²
                        </div>
                        <div style={{ padding: "6px 10px", borderRadius: 999, background: usesExpenseItems ? "#ecfdf3" : "#f9fafb", color: usesExpenseItems ? "#067647" : "#475467", fontSize: 12, fontWeight: 700 }}>
                            За статтями: {formatMoney(expensesNetworkPrice)} грн/м²
                        </div>
                        <div style={{ padding: "6px 10px", borderRadius: 999, background: "#f2f4f7", fontSize: 12, fontWeight: 700 }}>
                            Резервний тариф: {formatMoney(price)} грн/м²
                        </div>
                    </div>

                    <>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
                            <div style={{ color: "#777", fontSize: 12 }}>
                                Можна вимкнути окрему статтю без видалення. Клієнт побачить склад тарифу лише після відкриття деталей у своєму віджеті.
                            </div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <button className="add-btn" type="button" onClick={addExpenseItem} disabled={loading}><Plus size={16} /> Додати статтю</button>
                                <button className="add-btn" type="button" onClick={saveExpenseItems} disabled={loading}><Save size={16} /> Зберегти список</button>
                            </div>
                        </div>

                        <div className="table-responsive billing-table-responsive" style={{ marginTop: 12 }}>
                            <table className="billing-table billing-table--sticky" style={{ width: "100%", borderCollapse: "collapse", minWidth: 980 }}>
                                <thead>
                                    <tr style={{ textAlign: "left", borderBottom: "1px solid #eee" }}>
                                        <th style={{ padding: 10 }}>Назва</th>
                                        <th style={{ padding: 10 }}>грн/м²</th>
                                        <th style={{ padding: 10 }}>Опис / коментар</th>
                                        <th style={{ padding: 10 }}>Активна</th>
                                        <th style={{ padding: 10 }} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {expenseItems.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} style={{ padding: 14, color: "#777" }}>Статті тарифу ще не додані.</td>
                                        </tr>
                                    ) : expenseItems.map((item) => (
                                        <tr key={item.localKey} style={{ borderBottom: "1px solid #f2f2f2" }}>
                                            <td style={{ padding: 10 }}>
                                                <input value={item.title} onChange={(e) => setExpenseField(item.localKey, "title", e.target.value)} className="billing-input" />
                                            </td>
                                            <td style={{ padding: 10 }}>
                                                <input value={item.amount_per_m2} onChange={(e) => setExpenseField(item.localKey, "amount_per_m2", e.target.value)} className="billing-input" style={{ width: 120 }} />
                                            </td>
                                            <td style={{ padding: 10 }}>
                                                <input value={item.description} onChange={(e) => setExpenseField(item.localKey, "description", e.target.value)} className="billing-input" placeholder="Необов'язково" />
                                            </td>
                                            <td style={{ padding: 10 }}>
                                                <input type="checkbox" checked={item.status !== false} onChange={(e) => setExpenseField(item.localKey, "status", e.target.checked)} />
                                            </td>
                                            <td style={{ padding: 10 }}>
                                                <button type="button" className="icon-button gray-btn" onClick={() => removeExpenseItem(item.localKey)} title="Видалити статтю"><Trash2 size={16} /></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="billing-mobile-list billing-expense-mobile-list" style={{ marginTop: 12 }}>
                            {expenseItems.length === 0 ? <div className="billing-card" style={{ color: "#777" }}>Статті тарифу ще не додані.</div> : expenseItems.map((item) => (
                                <div key={item.localKey} className="billing-expense-card config-card">
                                    <button
                                        type="button"
                                        className="operator-card-action is-delete billing-expense-card__delete"
                                        onClick={() => removeExpenseItem(item.localKey)}
                                        title="Видалити статтю"
                                        aria-label="Видалити статтю"
                                    >
                                        <Trash2 size={16} />
                                    </button>

                                    <div className="billing-expense-card__title-row">
                                        <div className="billing-field billing-field--grow">
                                            <div className="billing-label">Назва</div>
                                            <input
                                                value={item.title}
                                                onChange={(e) => setExpenseField(item.localKey, "title", e.target.value)}
                                                className="billing-input"
                                            />
                                        </div>
                                        <label className="billing-expense-active">
                                            <span className="billing-label">Активна</span>
                                            <span className="billing-expense-active__control">
                                                <input
                                                    type="checkbox"
                                                    checked={item.status !== false}
                                                    onChange={(e) => setExpenseField(item.localKey, "status", e.target.checked)}
                                                />
                                                <span>{item.status !== false ? "Так" : "Ні"}</span>
                                            </span>
                                        </label>
                                    </div>

                                    <div className="billing-card-grid billing-card-grid--compact billing-expense-card__grid">
                                        <div className="billing-field">
                                            <div className="billing-label">грн/м²</div>
                                            <input
                                                value={item.amount_per_m2}
                                                onChange={(e) => setExpenseField(item.localKey, "amount_per_m2", e.target.value)}
                                                className="billing-input"
                                            />
                                        </div>
                                        <div className="billing-field billing-field--full">
                                            <div className="billing-label">Опис</div>
                                            <input
                                                value={item.description}
                                                onChange={(e) => setExpenseField(item.localKey, "description", e.target.value)}
                                                className="billing-input"
                                                placeholder="Необов'язково"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                </div>
            </BillingPanelShell>

            <BillingPanelShell
                open={showImportPanel}
                onClose={() => setShowImportPanel(false)}
                title="Імпорт виписки з файлу"
            >
                <div className="list-card operator-mobile-compact-card" style={{ marginBottom: isMobile ? 0 : 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <div>
                            {!isMobile ? <h3 style={{ marginTop: 0, marginBottom: 4 }}>Імпорт виписки з файлу</h3> : null}
                            <div className="billing-help-text" style={{ fontSize: 12 }}>
                                Завантаж CSV/TSV з клієнт-банку, перевір matched-рядки і лише потім рознеси оплату в систему.
                            </div>
                        </div>
                    </div>

                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
                        <input
                            type="file"
                            accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values"
                            onChange={handleImportFileChange}
                            disabled={loading || importBusy}
                        />
                        <button className="add-btn" type="button" onClick={previewImport} disabled={loading || importBusy || !importFile}>
                            {importBusy ? "Зчитування..." : "Перевірити виписку"}
                        </button>
                        {importFile ? <div style={{ color: "#555", fontSize: 12 }}>Файл: {importFile.name}</div> : null}
                    </div>

                    <div style={{ color: "#777", fontSize: 12, marginTop: 8 }}>
                        Підтримуються колонки типу: <strong>сума</strong>, <strong>особовий рахунок</strong>, <strong>квартира</strong>, <strong>локація</strong>, <strong>ПІБ/платник</strong>, <strong>призначення</strong>, <strong>client_id</strong>, <strong>дата</strong>.
                    </div>

                    {importPreview ? (
                        <>
                            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 12, fontSize: 13 }}>
                                <div><strong>Рядків:</strong> {importPreview.parsed_rows || 0}</div>
                                <div><strong>Знайдено:</strong> {importPreview.matched_rows || 0}</div>
                                <div><strong>Не знайдено:</strong> {importPreview.unmatched_rows || 0}</div>
                                <div><strong>У файлі:</strong> {formatMoney(importPreview.total_amount || 0)} грн</div>
                                <div><strong>Обрано:</strong> {selectedImportCount} рядків / {formatMoney(selectedImportAmount)} грн</div>
                            </div>

                            <div className="table-responsive billing-table-responsive" style={{ marginTop: 12 }}>
                                <table className="billing-table billing-table--sticky" style={{ width: "100%", borderCollapse: "collapse", minWidth: 1100 }}>
                                    <thead>
                                        <tr style={{ textAlign: "left", borderBottom: "1px solid #eee" }}>
                                            <th style={{ padding: 10 }}>Взяти</th>
                                            <th style={{ padding: 10 }}>Рядок</th>
                                            <th style={{ padding: 10 }}>Сума</th>
                                            <th style={{ padding: 10 }}>Платіж з файлу</th>
                                            <th style={{ padding: 10 }}>Збіг у системі</th>
                                            <th style={{ padding: 10 }}>Поточне оплачено</th>
                                            <th style={{ padding: 10 }}>Буде оплачено</th>
                                            <th style={{ padding: 10 }}>Статус</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(importPreview.items || []).map((item) => {
                                            const canApply = Boolean(item?.matched && item?.client_id);
                                            return (
                                                <tr key={item.row_index} style={{ borderBottom: "1px solid #f2f2f2", background: canApply ? "transparent" : "#fff8f8" }}>
                                                    <td style={{ padding: 10 }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={Boolean(selectedImportRows[item.row_index])}
                                                            disabled={!canApply || importBusy || loading}
                                                            onChange={(event) => toggleImportRow(item.row_index, event.target.checked)}
                                                        />
                                                    </td>
                                                    <td style={{ padding: 10 }}>#{item.row_index}</td>
                                                    <td style={{ padding: 10, whiteSpace: "nowrap" }}>{formatMoney(item.amount)} грн</td>
                                                    <td style={{ padding: 10 }}>
                                                        <div><strong>{item.raw_client_name || "-"}</strong></div>
                                                        {item.raw_personal_account ? <div style={{ color: "#777", fontSize: 12 }}>о/р: {item.raw_personal_account}</div> : null}
                                                        <div style={{ color: "#777", fontSize: 12 }}>
                                                            {[item.raw_location, item.raw_apartment ? `кв. ${item.raw_apartment}` : null].filter(Boolean).join(" · ") || "-"}
                                                        </div>
                                                        {item.payment_date ? <div style={{ color: "#777", fontSize: 12 }}>{item.payment_date}</div> : null}
                                                        {item.purpose ? <div style={{ color: "#777", fontSize: 12 }}>{item.purpose}</div> : null}
                                                    </td>
                                                    <td style={{ padding: 10 }}>
                                                        {item.matched ? (
                                                            <>
                                                                <div><strong>{item.client_name || "-"}</strong></div>
                                                                {item.personal_account ? <div style={{ color: "#777", fontSize: 12 }}>о/р: {item.personal_account}</div> : null}
                                                                <div style={{ color: "#777", fontSize: 12 }}>
                                                                    {[item.location_name, item.apartment ? `кв. ${item.apartment}` : null].filter(Boolean).join(" · ") || `client_id ${item.client_id}`}
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <span style={{ color: "#b42318" }}>Не знайдено</span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: 10, whiteSpace: "nowrap" }}>{formatMoney(item.current_paid_amount || 0)} грн</td>
                                                    <td style={{ padding: 10, whiteSpace: "nowrap" }}>{item.matched ? `${formatMoney(item.new_paid_amount || 0)} грн` : "-"}</td>
                                                    <td style={{ padding: 10 }}>
                                                        <div style={{ color: item.matched ? "#067647" : "#b42318", fontWeight: 600 }}>
                                                            {item.matched ? "Готово до рознесення" : "Потрібна перевірка"}
                                                        </div>
                                                        <div style={{ color: "#777", fontSize: 12 }}>{item.match_reason || "-"}</div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="billing-mobile-list" style={{ marginTop: 12 }}>
                                {(importPreview.items || []).map((item) => {
                                    const canApply = Boolean(item?.matched && item?.client_id);
                                    return (
                                        <div key={item.row_index} className="billing-card" style={{ border: canApply ? undefined : "1px solid #fecdca" }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                                                <div>
                                                    <div style={{ fontWeight: 800 }}>Рядок #{item.row_index}</div>
                                                    <div style={{ color: "#777", fontSize: 12 }}>{formatMoney(item.amount)} грн</div>
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(selectedImportRows[item.row_index])}
                                                    disabled={!canApply || importBusy || loading}
                                                    onChange={(event) => toggleImportRow(item.row_index, event.target.checked)}
                                                />
                                            </div>

                                            <div style={{ marginTop: 10, fontSize: 13 }}>
                                                <div><strong>З файлу:</strong> {item.raw_client_name || "-"}</div>
                                                {item.raw_personal_account ? <div style={{ color: "#777", fontSize: 12 }}>о/р: {item.raw_personal_account}</div> : null}
                                                <div style={{ color: "#777", fontSize: 12 }}>
                                                    {[item.raw_location, item.raw_apartment ? `кв. ${item.raw_apartment}` : null].filter(Boolean).join(" · ") || item.purpose || "-"}
                                                </div>
                                                <div style={{ marginTop: 8 }}><strong>У системі:</strong> {item.matched ? (item.client_name || "-") : "Не знайдено"}</div>
                                                {item.matched && item.personal_account ? <div style={{ color: "#777", fontSize: 12 }}>о/р: {item.personal_account}</div> : null}
                                                <div style={{ color: "#777", fontSize: 12 }}>
                                                    {item.matched ? ([item.location_name, item.apartment ? `кв. ${item.apartment}` : null].filter(Boolean).join(" · ") || `client_id ${item.client_id}`) : (item.match_reason || "-")}
                                                </div>
                                                <div style={{ marginTop: 8 }}><strong>Оплачено:</strong> {formatMoney(item.current_paid_amount || 0)} грн → {item.matched ? `${formatMoney(item.new_paid_amount || 0)} грн` : "-"}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
                                <div className="billing-help-text" style={{ fontSize: 12 }}>
                                    У рознесення підуть тільки позначені рядки зі знайденим клієнтом. Невпізнані рядки залишаться лише у превʼю для ручної перевірки.
                                </div>
                                <button className="add-btn" type="button" onClick={applyImport} disabled={loading || importBusy || selectedImportCount === 0}>
                                    {importBusy ? "Рознесення..." : `Рознести ${selectedImportCount} рядків`}
                                </button>
                            </div>
                        </>
                    ) : null}
                </div>
            </BillingPanelShell>

            <BillingPanelShell
                open={showLocationPrices}
                onClose={() => setShowLocationPrices(false)}
                title="Ціна за м² для локації"
            >
                <div className="list-card operator-mobile-compact-card" style={{ marginBottom: isMobile ? 0 : 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        {!isMobile ? <h3 style={{ marginTop: 0, marginBottom: 0 }}>Ціна за м² (для будинку / локації)</h3> : null}
                    </div>

                    <>
                        <div className="table-responsive billing-table-responsive" style={{ marginTop: 10 }}>
                            <table className="billing-table billing-table--sticky" style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
                                <thead>
                                    <tr style={{ textAlign: "left", borderBottom: "1px solid #eee" }}>
                                        <th style={{ padding: 10 }}>Локація</th>
                                        <th style={{ padding: 10 }}>Ціна за м²</th>
                                        <th style={{ padding: 10 }} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {locationRows.map((r) => {
                                        const v = locationEdits[r.location_id];
                                        const address = [r.city, r.street, r.house_number].filter(Boolean).join(", ");
                                        const effective = r.updated_at ? (r.price_per_m2 ?? 0) : effectiveNetworkPrice;
                                        return (
                                            <tr key={r.location_id} style={{ borderBottom: "1px solid #f2f2f2" }}>
                                                <td style={{ padding: 10 }}>
                                                    <strong>{r.location_name || ""}</strong>
                                                    {address ? <div style={{ color: "#777", fontSize: 12 }}>{address}</div> : null}
                                                </td>
                                                <td style={{ padding: 10 }}>
                                                    <input
                                                        value={v ?? String(effective)}
                                                        onChange={(ev) => setLocationField(r.location_id, ev.target.value)}
                                                        className="billing-input"
                                                        style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd", width: 140 }}
                                                    />
                                                </td>
                                                <td style={{ padding: 10 }}>
                                                    <button className="add-btn" onClick={() => saveLocationPrice(r)} disabled={loading}>Зберегти</button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="billing-mobile-list" style={{ marginTop: 10 }}>
                            {locationRows.map((r) => {
                                const v = locationEdits[r.location_id];
                                const address = [r.city, r.street, r.house_number].filter(Boolean).join(", ");
                                const effective = r.updated_at ? (r.price_per_m2 ?? 0) : effectiveNetworkPrice;
                                const isOverride = Boolean(r.updated_at);
                                return (
                                    <div key={r.location_id} className="billing-card">
                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis" }}>{r.location_name || ""}</div>
                                                {address ? <div style={{ color: "#777", fontSize: 12, marginTop: 2 }}>{address}</div> : null}
                                                {!isOverride ? (
                                                    <div style={{ color: "#777", fontSize: 12, marginTop: 6 }}>
                                                        Використовується діючий тариф мережі: <strong>{formatMoney(effectiveNetworkPrice)}</strong>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>

                                        <div className="billing-card-grid">
                                            <div className="billing-field">
                                                <div className="billing-label">Ціна за м²</div>
                                                <input
                                                    value={v ?? String(effective)}
                                                    onChange={(ev) => setLocationField(r.location_id, ev.target.value)}
                                                    className="billing-input"
                                                />
                                            </div>
                                            <div className="billing-actions">
                                                <button className="add-btn" onClick={() => saveLocationPrice(r)} disabled={loading}>Зберегти</button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                </div>
            </BillingPanelShell>

            {message ? <p>{message}</p> : null}
            {loading ? <p>⏳ Завантаження...</p> : null}

            <div className="list-card">
                <div className="table-responsive billing-table-responsive">
                    <table className="billing-table billing-table--sticky" style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
                        <thead>
                            <tr style={{ textAlign: "left", borderBottom: "1px solid #eee" }}>
                                <th style={{ padding: 10 }}>Клієнт</th>
                                <th style={{ padding: 10 }}>Кв.</th>
                                <th style={{ padding: 10 }}>О/р</th>
                                <th style={{ padding: 10 }}>Площа, м²</th>
                                <th style={{ padding: 10 }}>Нараховано</th>
                                <th style={{ padding: 10 }}>Оплачено</th>
                                <th style={{ padding: 10 }}>Борг</th>
                                <th style={{ padding: 10 }}>До оплати</th>
                                <th style={{ padding: 10 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((r) => {
                                const editKey = getRowEditKey(r);
                                const isEditing = editingRowKey === editKey;
                                const e = isEditing ? (edits[editKey] || {}) : {};
                                const area = isEditing ? (e.area_m2 ?? r.area_m2 ?? 0) : (r.area_m2 ?? 0);
                                const paid = isEditing ? (e.paid_amount ?? r.paid_amount ?? 0) : (r.paid_amount ?? 0);
                                const debt = isEditing ? (e.debt_amount ?? r.debt_amount ?? 0) : (r.debt_amount ?? 0);
                                const accrued = getAccruedAmount(r.price_per_m2, area);
                                const balance = getBalanceAmount({ price_per_m2: r.price_per_m2, area_m2: area, paid_amount: paid, debt_amount: debt });
                                const balanceView = getBalancePresentation(balance);
                                return (
                                    <tr key={editKey} style={{ borderBottom: "1px solid #f2f2f2" }}>
                                        <td style={{ padding: 10 }}>
                                            <strong>{r.client_name || ""}</strong>
                                            {r.location_name ? <div style={{ color: "#777", fontSize: 12 }}>{r.location_name}</div> : null}
                                        </td>
                                        <td style={{ padding: 10 }}>{r.apartment || ""}</td>
                                        <td style={{ padding: 10 }}>{r.personal_account || "-"}</td>
                                        <td style={{ padding: 10 }}>
                                            {isEditing ? (
                                                <input
                                                    value={area}
                                                    onChange={(ev) => setField(r, "area_m2", ev.target.value)}
                                                    className="billing-input"
                                                    style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd", width: 120 }}
                                                />
                                            ) : (
                                                formatMoney(area)
                                            )}
                                        </td>
                                        <td style={{ padding: 10, whiteSpace: "nowrap" }}>
                                            <strong>{formatMoney(accrued)} грн</strong>
                                        </td>
                                        <td style={{ padding: 10 }}>
                                            {isEditing ? (
                                                <input
                                                    value={paid}
                                                    onChange={(ev) => setField(r, "paid_amount", ev.target.value)}
                                                    className="billing-input"
                                                    style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd", width: 120 }}
                                                />
                                            ) : (
                                                `${formatMoney(paid)} грн`
                                            )}
                                        </td>
                                        <td style={{ padding: 10 }}>
                                            {isEditing ? (
                                                <input
                                                    value={debt}
                                                    onChange={(ev) => setField(r, "debt_amount", ev.target.value)}
                                                    className="billing-input"
                                                    style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd", width: 120 }}
                                                />
                                            ) : (
                                                `${formatMoney(debt)} грн`
                                            )}
                                        </td>
                                        <td style={{ padding: 10 }}>
                                            <div style={{ fontWeight: 700, color: balanceView.color }}>{balanceView.label}</div>
                                            <div style={{ fontSize: 12, whiteSpace: "nowrap" }}>{formatMoney(balanceView.amount)} грн</div>
                                        </td>
                                        <td style={{ padding: 10 }}>
                                            {isEditing ? (
                                                <div style={{ display: "flex", gap: 6 }}>
                                                    <button className="icon-button" onClick={() => saveClient(r)} disabled={loading} title="Зберегти"><Save size={14} /></button>
                                                    <button className="icon-button" onClick={() => cancelEditRow(r)} disabled={loading} title="Скасувати"><X size={14} /></button>
                                                </div>
                                            ) : (
                                                <button className="icon-button" onClick={() => startEditRow(r)} disabled={loading || (editingRowKey && editingRowKey !== editKey)} title="Редагувати"><Edit size={14} /></button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="billing-mobile-list operator-accordion-list">
                    {visible.map((r) => {
                        const editKey = getRowEditKey(r);
                        const isEditing = editingRowKey === editKey;
                        const isExpanded = expandedBillingKey === editKey || isEditing;
                        const e = isEditing ? (edits[editKey] || {}) : {};
                        const area = isEditing ? (e.area_m2 ?? r.area_m2 ?? 0) : (r.area_m2 ?? 0);
                        const paid = isEditing ? (e.paid_amount ?? r.paid_amount ?? 0) : (r.paid_amount ?? 0);
                        const debt = isEditing ? (e.debt_amount ?? r.debt_amount ?? 0) : (r.debt_amount ?? 0);
                        const accrued = getAccruedAmount(r.price_per_m2, area);
                        const balance = getBalanceAmount({ price_per_m2: r.price_per_m2, area_m2: area, paid_amount: paid, debt_amount: debt });
                        const balanceView = getBalancePresentation(balance);
                        const metaParts = [
                            r.location_name || null,
                            r.apartment ? `кв. ${r.apartment}` : null,
                            r.personal_account ? `о/р: ${r.personal_account}` : null,
                        ].filter(Boolean);
                        return (
                            <div key={editKey} className="operator-accordion-item billing-card billing-card--view config-card">
                                <button
                                    type="button"
                                    className="operator-accordion-head"
                                    onClick={() => toggleBillingExpanded(editKey, isEditing)}
                                    aria-expanded={isExpanded}
                                >
                                    <div className="operator-accordion-head-main">
                                        <div className="operator-accordion-title">{r.client_name || "Без імені"}</div>
                                        <div className="operator-accordion-meta">
                                            {metaParts.join(" · ") || "Без адреси"}
                                        </div>
                                        <div className="operator-accordion-preview" style={{ color: balanceView.color, fontWeight: 700 }}>
                                            {balanceView.label}: {formatMoney(balanceView.amount)} грн
                                        </div>
                                    </div>
                                    <div className="operator-accordion-caret">{isExpanded ? "▾" : "▸"}</div>
                                </button>

                                <OperatorCardActions
                                    editing={isEditing}
                                    onEdit={() => startEditRow(r)}
                                    onSave={() => saveClient(r)}
                                    onCancel={() => cancelEditRow(r)}
                                    disabled={loading || (editingRowKey && editingRowKey !== editKey && !isEditing)}
                                />

                                {isExpanded ? (
                                    <div className="operator-accordion-body">
                                        <div className="billing-card-grid billing-card-grid--compact">
                                            <div className="billing-field">
                                                <div className="billing-label">Площа, м²</div>
                                                {isEditing ? (
                                                    <input
                                                        value={area}
                                                        onChange={(ev) => setField(r, "area_m2", ev.target.value)}
                                                        className="billing-input"
                                                    />
                                                ) : (
                                                    <div className="billing-readonly-value">{formatMoney(area)}</div>
                                                )}
                                            </div>
                                            <div className="billing-field">
                                                <div className="billing-label">Нараховано</div>
                                                <div className="billing-readonly-value">{formatMoney(accrued)} грн</div>
                                            </div>
                                            <div className="billing-field">
                                                <div className="billing-label">Оплачено</div>
                                                {isEditing ? (
                                                    <input
                                                        value={paid}
                                                        onChange={(ev) => setField(r, "paid_amount", ev.target.value)}
                                                        className="billing-input"
                                                    />
                                                ) : (
                                                    <div className="billing-readonly-value">{formatMoney(paid)} грн</div>
                                                )}
                                            </div>
                                            <div className="billing-field">
                                                <div className="billing-label">Борг</div>
                                                {isEditing ? (
                                                    <input
                                                        value={debt}
                                                        onChange={(ev) => setField(r, "debt_amount", ev.target.value)}
                                                        className="billing-input"
                                                    />
                                                ) : (
                                                    <div className="billing-readonly-value">{formatMoney(debt)} грн</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default OperatorBilling;
